import type { LocationQueryRaw } from 'vue-router';
import type {
  ControlGroup,
  ControlKind,
  ControlPendingVideo,
  GestureControlMetrics,
  GestureMatrix,
  GestureMatrixKind,
  GroupBatch,
} from '../model.js';
import {
  CONTROL_KIND_TITLES,
  type ControlFigure,
  controlExecutionLine,
  controlFigures,
} from './control-groups.js';
import { executionOptionLabel } from './group-history.js';
import type { KpiTone } from './kpi-tone.js';
import { batchesNewestFirst } from './latest-execution.js';
import { figureField } from './metric-explanations.js';
import { directedTrend, type MetricTrend } from './metric-trend.js';
import { metricsReading } from './metrics-reading.js';
import { DEFAULT_SCORE_METHOD, type MetricStatus } from './metrics-series.js';
import { formatPct, isProductMetric, type ProductMetricKey } from './product-metric.js';
import { rawValue } from './version-series.js';

/**
 * A control group's own page (product owner, 2026-10-06): what it measures, whether its videos have
 * what it needs, its figures against their targets and how they moved since the execution before,
 * its history, and — for Gestos — its figures opened by video and gesture
 * (`gesture-matrix-layout.ts`). Every word and number on it is decided here; the view lays them out.
 *
 * ## The page is the kind's
 *
 * Addressed by its kind (`/virtual-profiler/control/gestures`), as upstream serves the controls: a
 * control is not a group and has no id (2026-10-28) — its videos, executions and measurements are
 * read by its kind, as the owner `{ controlKind }`. An address with any other kind is a page that
 * does not exist, said as such, never a guess at the nearest one.
 *
 * ## «The execution before» is the one before that fits
 *
 * A trend compares the execution the figures come from — upstream's latest that fits the kind —
 * with the one before it that fits too: finished, not cancelled, and run with the engine the kind
 * measures (the gestures engine for Gestos, coherence for the other two). The history does not say
 * where an execution's gestures came from, so Semántica and Veracidad are told apart by their
 * groups, never by an execution: each kind has its own. An execution whose engines were not
 * recorded is not taken — a trend against something that may have measured something else would
 * be an invented one — and with nothing before, there is no trend.
 *
 * Both sides are upstream's: the coherence figures before come from the history, in the normal
 * reading and with the method the control's own were scored with — never «Métricas globales», which
 * the control's are not —; the gestures figures before are that execution's gesture map totals,
 * which upstream works out against the labels as they are now, the same ruler as the latest's.
 */

/** Its route, by name: the Dataset's sidebar and the control cards link to it. */
export const CONTROL_DETAIL_ROUTE = 'virtual-profiler-control-detail';

/**
 * The kind as the address spells it: the screens say «Veracidad», so its page is
 * `/virtual-profiler/control/veracity` (product owner, 2026-10-20), while the kind upstream — and
 * every request — stays `coherence`. Only the address changes; nothing stores it.
 */
const CONTROL_SLUGS: Readonly<Record<ControlKind, string>> = Object.freeze({
  gestures: 'gestures',
  gesture_alerts: 'gesture-alerts',
  semantics: 'semantics',
  coherence: 'veracity',
});

/** The address's word for a kind. */
export function controlSlug(kind: ControlKind): string {
  return CONTROL_SLUGS[kind];
}

export function controlDetailLocation(kind: ControlKind): {
  name: string;
  params: { kind: string };
} {
  return { name: CONTROL_DETAIL_ROUTE, params: { kind: controlSlug(kind) } };
}

/**
 * The kind in the address, or null for one that is not a control group: its slug, or the kind
 * itself — `/control/coherence` is how the links before 2026-10-20 spell Veracidad.
 */
export function controlKindFrom(raw: unknown): ControlKind | null {
  if (typeof raw !== 'string') return null;

  const bySlug = (Object.keys(CONTROL_SLUGS) as ControlKind[]).find(
    (kind) => CONTROL_SLUGS[kind] === raw,
  );

  if (bySlug) return bySlug;

  return Object.hasOwn(CONTROL_KIND_TITLES, raw) ? (raw as ControlKind) : null;
}

/** What the page says for an address that names no control group. */
export function unknownControlNotice(raw: unknown): {
  title: string;
  description: string;
} {
  const named = typeof raw === 'string' && raw.trim() !== '' ? `«${raw}» no es un control. ` : '';

  return {
    title: 'Ese control no existe',
    description: `${named}Hay cuatro: Gestos, Alertas gestuales, Semántica y Veracidad.`,
  };
}

/**
 * Whether the kind's page opens its figures into a gesture map. Only Gestos: Veracidad is measured
 * against truth or lie only, Semántica's page leaves the map out (product owner, 2026-10-06), and
 * Alertas gestuales has no map of its own yet.
 */
export function hasGestureMap(kind: ControlKind): kind is GestureMatrixKind {
  return kind === 'gestures';
}

// ── The executions that fit ──────────────────────────────────────────────────

/** The engine whose output the kind measures: what an execution must have run to fit it. */
const MEASURED_ENGINE: Readonly<Record<ControlKind, string>> = Object.freeze({
  gestures: 'gestures',
  gesture_alerts: 'gestures',
  semantics: 'coherence',
  coherence: 'coherence',
});

/** Finished, not cancelled, and run with the engine the kind measures, as far as it recorded. */
export function fitsControl(batch: GroupBatch, kind: ControlKind): boolean {
  return batch.complete && !batch.cancelled && batch.engines.includes(MEASURED_ENGINE[kind]);
}

/** The group's executions that fit the kind, newest first. */
export function controlExecutions(
  batches: readonly GroupBatch[] | null | undefined,
  kind: ControlKind,
): GroupBatch[] {
  return batchesNewestFirst(batches).filter((batch) => fitsControl(batch, kind));
}

/**
 * The execution that fits the kind right before `latest` (upstream's, the one the figures come
 * from); null when `latest` is not among the fitting ones, or is the first of them.
 */
export function previousControlExecution(
  batches: readonly GroupBatch[] | null | undefined,
  kind: ControlKind,
  latest: string | null,
): GroupBatch | null {
  if (latest === null) return null;

  const fitting = controlExecutions(batches, kind);
  const index = fitting.findIndex((batch) => batch.batch === latest);

  return index === -1 ? null : (fitting[index + 1] ?? null);
}

/** «Frente a 20 sept, 11:00 · Gestos base · v5»: what each trend is measured against. */
export function trendBasis(previous: GroupBatch | null): string {
  return previous
    ? `Frente a la ejecución anterior: ${executionOptionLabel(previous)}`
    : 'Sin una ejecución anterior con la que comparar';
}

// ── The figures, against their targets and the execution before ─────────────

/** Figures by their key, as `ControlFigure.key` names them; null where there is none. */
export type ControlFigureValues = Readonly<Record<string, number | null>>;

/** The gestures figures of a gesture map's totals, by the keys of the Gestos figures. */
export function gestureFigureValues(totals: GestureControlMetrics): ControlFigureValues {
  return {
    detection_pct: totals.detectionPct,
    false_positive_pct: totals.falsePositivePct,
    correct_videos_pct: totals.correctVideosPct,
  };
}

/** The method the control's coherence figures were scored with; the default before any. */
export function controlMethod(control: ControlGroup): string {
  return control.metrics?.kind === 'coherence' ? control.metrics.method : DEFAULT_SCORE_METHOD;
}

/**
 * An execution's product figures under one method, in the normal reading unless `global` asks for
 * «Métricas globales»; null when it was not measured with that method.
 */
export function coherenceFigureValues(
  batch: GroupBatch,
  method: string,
  global = false,
): ControlFigureValues | null {
  const block = batch.metrics?.methods[method];

  if (!block) return null;

  const read = (key: string) => rawValue(block, figureField(key, global));

  return {
    incoherences_detected_pct: read('incoherences_detected_pct'),
    false_positives_pct: read('false_positives_pct'),
    correct_pct: read('correct_pct'),
  };
}

/**
 * The figures of the execution before, by the kind's own reading: that execution's gesture map
 * totals for Gestos — only once the map asked for is that execution's —, its product figures for the
 * other two under the user's preferred method and reading, as the figures beside them
 * (`controlFigures`), so a trend never compares two rulers. Null with no execution before, or
 * nothing read of it yet.
 */
export function previousFigures(
  control: ControlGroup,
  previous: GroupBatch | null,
  previousMap: GestureMatrix | null | undefined,
): ControlFigureValues | null {
  if (!previous) return null;

  if (control.kind === 'gestures') {
    return previousMap?.batch === previous.batch ? gestureFigureValues(previousMap.totals) : null;
  }
  // The history carries no alert-control figures: Alertas gestuales has no trend.
  if (control.kind === 'gesture_alerts') return null;

  return coherenceFigureValues(previous, metricsReading.method, metricsReading.global);
}

export interface ControlFigureTile extends ControlFigure {
  /** «85,7 %»; null when there is no figure, which the tile says as «—». */
  shown: string | null;
  /** «Objetivo ≥ 80 %». */
  caption: string;
  /** The verdict against the target, as a tone and its reason: the tile words it. */
  tone: KpiTone;
  /** Against the execution before that fits; null without one, or without either figure. */
  trend: MetricTrend | null;
  /** One of the product's three, which keep their identity; null for the gestures figures. */
  productMetric: ProductMetricKey | null;
}

/**
 * Missing a target is a warning and never a danger: the targets are the product owner's
 * aspirations, and this module's red is spoken for (`kpi-tone.ts`). No figure, no verdict.
 */
const VERDICT_TONE: Readonly<Record<MetricStatus, KpiTone>> = Object.freeze({
  ok: { tone: 'success', reason: 'meets_target', caveat: null },
  ko: { tone: 'warning', reason: 'misses_target', caveat: null },
  none: { tone: null, reason: 'no_value', caveat: null },
});

/** The control's three figures, each with its target, its verdict and how it moved. */
export function controlFigureTiles(
  control: ControlGroup,
  previous: ControlFigureValues | null,
): ControlFigureTile[] {
  return figureTiles(controlFigures(control), previous);
}

function figureTiles(
  figures: readonly ControlFigure[],
  previous: ControlFigureValues | null,
): ControlFigureTile[] {
  return figures.map((figure) => ({
    ...figure,
    shown: figure.value === null ? null : formatPct(figure.value),
    caption: `Objetivo ${figure.targetText}`,
    tone: VERDICT_TONE[figure.status],
    trend: directedTrend(figure.direction, figure.value, previous?.[figure.key] ?? null),
    productMetric: isProductMetric(figure.key) ? figure.key : null,
  }));
}

// ── Labelling what is pending ────────────────────────────────────────────────

/** What a pending video lacks that a person writes by hand; the transcription is not. */
const LABELLED_BY_HAND: ReadonlySet<string> = new Set([
  'gesture_labels',
  'gesture_labelling_complete',
  'alert_labels',
  'truth_label',
]);

/** A pending video's page, or null for a member that is not a video and has none. */
export function pendingVideoLocation(
  video: ControlPendingVideo,
): { name: string; params: { videoId: string } } | null {
  return video.sourceType === 'manual_analysis'
    ? {
        name: 'virtual-profiler-video-detail',
        params: { videoId: String(video.sourceId) },
      }
    : null;
}

export type LabelTarget =
  | {
      kind: 'video';
      video: ControlPendingVideo;
      location: {
        name: string;
        params: { kind: string };
        query: { tab: 'labelling'; video: string };
      };
    }
  | { kind: 'none'; reason: string };

/**
 * Where «Etiquetar» opens: the control's own «Etiquetado» tab (`lib/control-labelling.ts`), on the
 * first pending video that lacks something written by hand, in the pending list's order — and from
 * there the stepper walks the rest. Undefined is a list still on its way — unless its read failed,
 * which is said, or the button would wait for ever. A list it has outlives a refetch that failed,
 * and is still good.
 */
export function labelTarget(
  kind: ControlKind,
  pending: readonly ControlPendingVideo[] | undefined,
  failed = false,
): LabelTarget {
  if (pending === undefined) {
    return {
      kind: 'none',
      reason: failed
        ? 'No se han podido leer los vídeos pendientes'
        : 'Leyendo los vídeos pendientes…',
    };
  }
  if (pending.length === 0) return { kind: 'none', reason: 'No hay vídeos pendientes' };

  const video = pending.find(
    (candidate) =>
      candidate.sourceType === 'manual_analysis' &&
      candidate.missing.some((missing) => LABELLED_BY_HAND.has(missing)),
  );

  if (!video) {
    return {
      kind: 'none',
      reason: 'A los pendientes no les falta nada que se etiquete a mano: esperan su transcripción',
    };
  }

  return {
    kind: 'video',
    video,
    location: {
      name: CONTROL_DETAIL_ROUTE,
      params: { kind: controlSlug(kind) },
      query: { tab: 'labelling', video: String(video.sourceId) },
    },
  };
}

/** «Última ejecución: 28 sept, 12:30 · …», over the figures it gave. */
export function controlExecutionHeadline(control: Pick<ControlGroup, 'latestExecution'>): string {
  return `Última ejecución: ${controlExecutionLine(control.latestExecution)}`;
}

// ── The execution the map shows ──────────────────────────────────────────────

/**
 * Whether the map can be asked for: a kind with one, its control read — the latest execution comes
 * from it — and, when the address names an execution, the history that offers it read too. Asked
 * before, `chosenBatch` falls back to the latest, and the map is read for an execution nobody asked
 * for and thrown away.
 */
export function matrixReady(state: {
  hasMap: boolean;
  controlLoaded: boolean;
  batchInAddress: boolean;
  historyPending: boolean;
}): boolean {
  return state.hasMap && state.controlLoaded && (!state.batchInAddress || !state.historyPending);
}

/** The address parameter of the map's execution: absent is the latest, so a plain link opens it. */
export const CONTROL_BATCH_PARAM = 'batch';

export interface BatchOption {
  value: string;
  label: string;
}

/**
 * The executions the map can show, newest first, each as a picker says it: the ones that fit the
 * kind, and the one the figures come from even before the history has it — it is the default.
 */
export function controlBatchOptions(
  batches: readonly GroupBatch[] | null | undefined,
  kind: ControlKind,
  latest: string | null,
): BatchOption[] {
  const options = controlExecutions(batches, kind).map((batch) => ({
    value: batch.batch,
    label: executionOptionLabel(batch),
  }));

  if (latest === null || options.some((option) => option.value === latest)) return options;

  const known = (batches ?? []).find((batch) => batch.batch === latest);

  return [{ value: latest, label: known ? executionOptionLabel(known) : latest }, ...options];
}

/** The execution the address names when it is one of the options; the latest otherwise. */
export function chosenBatch(
  raw: unknown,
  options: readonly BatchOption[],
  latest: string | null,
): string | null {
  if (typeof raw === 'string' && options.some((option) => option.value === raw)) return raw;

  return latest ?? options[0]?.value ?? null;
}

/** The address with that execution on the map; the latest leaves the parameter out. */
export function withControlBatch(
  query: Readonly<LocationQueryRaw>,
  batch: string | null,
  latest: string | null,
): LocationQueryRaw {
  const next: LocationQueryRaw = { ...query };

  if (batch === null || batch === latest) delete next[CONTROL_BATCH_PARAM];
  else next[CONTROL_BATCH_PARAM] = batch;

  return next;
}

// ── «Ver» on an execution of the history ─────────────────────────────────────

export type ExecutionView =
  | { kind: 'map'; batch: string }
  | {
      kind: 'videos';
      location: {
        name: string;
        params: { kind: string };
        query: { batch: string; tab: 'videos' };
      };
    };

/**
 * Where «Ver» on an execution of the history takes it: onto this page's map when the map can show
 * it; otherwise onto this page's videos, on that execution — a Veracidad execution, or one that
 * does not fit the kind, has no map here, and the videos tab lists its runs one by one. By kind
 * either way: a control has no group page to fall back to (2026-10-28).
 */
export function executionView(
  kind: ControlKind,
  batch: string,
  options: readonly BatchOption[],
): ExecutionView {
  if (hasGestureMap(kind) && options.some((option) => option.value === batch)) {
    return { kind: 'map', batch };
  }

  return {
    kind: 'videos',
    location: {
      ...controlDetailLocation(kind),
      query: { batch, tab: 'videos' },
    },
  };
}
